/**
 * Panel definitions — the single source of truth for every panel type.
 *
 * Shared field factories keep the definitions terse and, more importantly,
 * keep the same field meaning the same everywhere. If "limit" needs a new
 * bound, it changes in one place and all three consumers follow.
 */

import type { ConstraintDef, FieldDef, PanelDef } from './types';
import { FORMATS } from './units';

// ── Shared field factories ─────────────────────────────────────────────────

export const F = {
  kind: (kinds: string[]): FieldDef => ({
    path: 'dataSource.kind',
    label: 'Data source',
    kind: 'select',
    default: kinds[0],
    required: true,
    options: {
      kind: 'static',
      options: [
        { value: 'perf', label: 'Performance (cost / clicks / leads / impressions)' },
        { value: 'leads', label: 'Leads' },
        { value: 'pacing', label: 'Pacing' },
        { value: 'static', label: 'Static (no data feed)' },
      ].filter((o) => kinds.includes(o.value)),
    },
  }),

  metric: (opts: { default?: string; label?: string } = {}): FieldDef => ({
    path: 'dataSource.metric',
    label: opts.label ?? 'Metric',
    kind: 'select',
    default: opts.default ?? 'leads',
    required: true,
    help: 'What this panel measures. The available display formats follow from it.',
    options: { kind: 'derived', from: 'dataSource.kind', via: 'metricsForKind' },
  }),

  dimension: (opts: { default?: string; label?: string } = {}): FieldDef => ({
    path: 'dataSource.dimension',
    label: opts.label ?? 'Group by',
    kind: 'select',
    default: opts.default ?? 'model',
    required: true,
    options: { kind: 'derived', from: 'dataSource.kind', via: 'dimensionsForKind' },
  }),

  dimensionY: (opts: { default?: string } = {}): FieldDef => ({
    path: 'dataSource.dimensionY',
    label: 'Rows (second dimension)',
    kind: 'select',
    default: opts.default ?? 'model',
    required: true,
    options: { kind: 'derived', from: 'dataSource.kind', via: 'dimensionsForKind' },
  }),

  /**
   * `from` names the metric field this format renders. It is a parameter, not a
   * constant, because a panel can format a metric that does not live at
   * dataSource.metric — the matrix formats its cell value, not its colour metric.
   */
  format: (path = 'config.format', label = 'Format', from = 'dataSource.metric'): FieldDef => ({
    path,
    label,
    kind: 'select',
    default: 'I',
    required: true,
    help: 'Only formats that suit the selected metric are offered.',
    options: { kind: 'derived', from, via: 'formatsForMetric' },
  }),

  limit: (def = 10): FieldDef => ({
    path: 'dataSource.limit',
    label: 'Max rows',
    kind: 'number',
    default: def,
    min: 1,
    max: 200,
    step: 1,
  }),

  threshold: (): FieldDef => ({
    path: 'config.threshold',
    label: 'Conditional colour',
    kind: 'threshold',
    help: 'Values at or better than "good" render green; at or worse than "bad" render red. Per-brand defaults apply when unset.',
  }),

  /**
   * Every data panel's own window. `inherit` is the default and is what a
   * panel written before panels had a range means — the dashboard decides.
   * Declared ONCE here so adding it to a panel type is one line rather than a
   * new form control, a new validation rule and a new renderer branch.
   */
  dateRange: (): FieldDef => ({
    path: 'dataSource.dateRange',
    label: 'Date range',
    kind: 'select',
    default: 'inherit',
    help: 'Leave on "Same as dashboard" unless this panel should show a different period from the rest.',
    options: {
      kind: 'static',
      options: [
        { value: 'inherit', label: 'Same as dashboard' },
        { value: 'lcw', label: 'Last complete week' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: '90d', label: 'Last 90 days' },
        { value: 'mtd', label: 'Month to date' },
        { value: 'qtd', label: 'Quarter to date' },
        { value: 'ytd', label: 'Year to date' },
        { value: 'all', label: 'All time' },
      ],
    },
  }),

  compare: (): FieldDef => ({
    path: 'dataSource.compare',
    label: 'Compare against',
    kind: 'select',
    default: 'none',
    help: 'Adds a period-over-period figure to this panel.',
    options: {
      kind: 'static',
      options: [
        { value: 'none', label: 'No comparison' },
        { value: 'previous', label: 'Previous period' },
        { value: 'last_year', label: 'Same period last year' },
      ],
    },
  }),

  showTrend: (): FieldDef => ({
    path: 'config.showTrend',
    label: 'Show period-over-period trend',
    kind: 'boolean',
    default: true,
  }),

  axisFormat: (): FieldDef => ({
    path: 'config.axisFormat',
    label: 'Axis format',
    kind: 'select',
    default: 'I',
    options: { kind: 'derived', from: 'dataSource.metric', via: 'formatsForMetric' },
  }),

  colorBy: (options: { value: string; label: string }[], def: string): FieldDef => ({
    path: 'config.colorBy',
    label: 'Colour by',
    kind: 'select',
    default: def,
    options: { kind: 'static', options },
  }),

  refLines: (): FieldDef => ({
    path: 'config.refLines',
    label: 'Reference lines',
    kind: 'threshold',
    help: 'Horizontal marker lines, e.g. a target CPL.',
  }),
};

/** The unit-class rule, spelled once. */
const unitMatch = (formatField: string): ConstraintDef => ({
  rule: 'unitMatch',
  metricField: 'dataSource.metric',
  formatField,
  message:
    'That display format cannot represent this metric — a count cannot be shown as currency, and a currency cannot be shown as a plain integer.',
});

/** The feed-compatibility rule, spelled once. */
const dimensionValid = (field = 'dataSource.dimension'): ConstraintDef => ({
  rule: 'validForKind',
  field,
  kindField: 'dataSource.kind',
  via: 'dimensionsForKind',
  message: 'This data source cannot group by that dimension.',
});

// ── Panel definitions ──────────────────────────────────────────────────────

export const KPI: PanelDef = {
  type: 'kpi',
  label: 'KPI',
  description: 'Single metric with optional trend',
  defaultSize: { w: 3, h: 2 },
  kinds: ['perf', 'leads', 'pacing'],
  fields: [
    F.dateRange(),
    F.compare(),
    F.kind(['perf', 'leads', 'pacing']),
    F.metric(),
    F.format(),
    F.threshold(),
    F.showTrend(),
  ],
  constraints: [unitMatch('config.format')],
  consumes: [
    'dataSource.kind',
    'dataSource.metric',
    'config.format',
    'dataSource.dateRange',
    'dataSource.compare',
  ],
};

export const BAR_H: PanelDef = {
  type: 'bar_h',
  label: 'Bar (horizontal)',
  description: 'Top-N by metric',
  defaultSize: { w: 6, h: 4 },
  kinds: ['perf', 'leads'],
  fields: [
    F.dateRange(),
    F.kind(['perf', 'leads']),
    F.dimension(),
    F.metric(),
    F.limit(10),
    F.axisFormat(),
    F.colorBy(
      [
        { value: 'index', label: 'Palette order' },
        { value: 'cpl', label: 'CPL thresholds' },
        { value: 'goodbad', label: 'Good / bad' },
      ],
      'index',
    ),
    F.refLines(),
  ],
  constraints: [unitMatch('config.axisFormat'), dimensionValid()],
  consumes: [
    'dataSource.kind',
    'dataSource.dimension',
    'dataSource.metric',
    'dataSource.limit',
    'dataSource.dateRange',
  ],
};

export const BAR_V: PanelDef = {
  type: 'bar_v',
  label: 'Bar (vertical)',
  description: 'Categorical comparison',
  defaultSize: { w: 6, h: 4 },
  kinds: ['perf', 'leads'],
  fields: [
    F.dateRange(),
    F.kind(['perf', 'leads']),
    F.dimension(),
    F.metric(),
    F.limit(10),
    F.axisFormat(),
    F.colorBy(
      [
        { value: 'index', label: 'Palette order' },
        { value: 'cpl', label: 'CPL thresholds' },
        { value: 'goodbad', label: 'Good / bad' },
      ],
      'index',
    ),
    F.refLines(),
  ],
  constraints: [unitMatch('config.axisFormat'), dimensionValid()],
  consumes: [
    'dataSource.kind',
    'dataSource.dimension',
    'dataSource.metric',
    'dataSource.limit',
    'dataSource.dateRange',
  ],
};

export const LINE: PanelDef = {
  type: 'line',
  label: 'Line',
  description: 'Time series trend',
  defaultSize: { w: 12, h: 4 },
  kinds: ['perf', 'leads'],
  fields: [
    F.dateRange(),
    F.kind(['perf', 'leads']),
    F.dimension({ default: 'week' }),
    F.metric(),
    F.axisFormat(),
    {
      path: 'config.series',
      label: 'Series',
      kind: 'series',
      help: 'Plot several metrics together. Metrics on the same axis must share a unit.',
      options: { kind: 'derived', from: 'dataSource.kind', via: 'metricsForKind' },
    },
    F.refLines(),
  ],
  constraints: [
    unitMatch('config.axisFormat'),
    dimensionValid(),
    {
      rule: 'sameAxisUnits',
      seriesField: 'config.series',
      message:
        'Metrics on the same axis must use the same unit. Put counts and money on separate axes.',
    },
  ],
  consumes: [
    'dataSource.kind',
    'dataSource.dimension',
    'dataSource.metric',
    'config.series',
    'dataSource.dateRange',
  ],
};

/**
 * PIE — the panel that produced "a spend chart plotting lead share".
 * `dataSource.metric` is in `consumes`, so the contract test fails until the
 * resolver actually honours it rather than hardcoding leads.
 */
export const PIE: PanelDef = {
  type: 'pie',
  label: 'Pie',
  description: 'Categorical breakdown',
  defaultSize: { w: 4, h: 4 },
  kinds: ['perf', 'leads'],
  fields: [
    F.dateRange(),
    F.kind(['perf', 'leads']),
    F.dimension({ default: 'channel' }),
    F.metric(),
    F.limit(10),
  ],
  constraints: [dimensionValid()],
  consumes: [
    'dataSource.kind',
    'dataSource.dimension',
    'dataSource.metric',
    'dataSource.limit',
    'dataSource.dateRange',
  ],
};

export const TABLE: PanelDef = {
  type: 'table',
  label: 'Table',
  description: 'Sortable rows',
  defaultSize: { w: 6, h: 4 },
  kinds: ['perf', 'leads'],
  fields: [
    F.dateRange(),
    F.kind(['perf', 'leads']),
    F.dimension(),
    F.limit(50),
    {
      path: 'config.columns',
      label: 'Columns',
      kind: 'columns',
    },
    {
      // A composite { key, dir } — TableConfig.defaultSort has always been an
      // object naming a column plus a direction, not a bare direction. Modelling
      // it as a plain asc/desc select was a schema bug that rejected every
      // shipped table.
      path: 'config.defaultSort',
      label: 'Default sort',
      kind: 'sort',
      help: 'Which column the table sorts by when it first loads, and in which direction.',
    },
  ],
  constraints: [dimensionValid()],
  consumes: ['dataSource.kind', 'dataSource.dimension', 'dataSource.limit', 'dataSource.dateRange'],
};

export const MATRIX: PanelDef = {
  type: 'matrix',
  label: 'Matrix',
  description: 'Heatmap grid',
  defaultSize: { w: 12, h: 5 },
  kinds: ['perf'],
  fields: [
    F.dateRange(),
    F.kind(['perf']),
    F.dimension({ default: 'channel', label: 'Columns' }),
    F.dimensionY({ default: 'model' }),
    {
      // NOT the displayed value. A matrix cell always shows the lead count;
      // this metric drives the cell's COLOUR. The shipped MG matrices say so in
      // their own titles — "Model × Channel — Leads (CPL color)" — and the
      // resolver agrees: it renders c.leads and colours by cellMetric.
      path: 'config.cellMetric',
      label: 'Colour cells by',
      kind: 'select',
      default: 'cpl',
      required: true,
      help: 'Cells show the lead count; this chooses the metric that colours them.',
      options: { kind: 'derived', from: 'dataSource.kind', via: 'metricsForKind' },
    },
    {
      // Formats the DISPLAYED value, which is a lead count, so only
      // count-capable formats are legal. Deliberately NOT derived from
      // cellMetric — pairing them would force MG's leads-coloured-by-CPL
      // matrix to render its lead counts as currency.
      path: 'config.cellFormat',
      label: 'Cell format',
      kind: 'select',
      default: 'I',
      required: true,
      options: {
        kind: 'static',
        options: [
          { value: 'I', label: 'Integer (1,234)' },
          { value: 'N', label: 'Compact (1.2k)' },
        ],
      },
    },
    F.colorBy(
      [
        { value: 'global', label: 'Global thresholds' },
        { value: 'column-relative', label: 'Relative to each column' },
      ],
      'global',
    ),
  ],
  constraints: [
    // No unitMatch here on purpose: cellFormat renders the lead count, while
    // cellMetric only selects the colour scale. Binding them was a schema bug
    // that would have rejected every matrix MG actually ships.
    dimensionValid(),
    dimensionValid('dataSource.dimensionY'),
    {
      rule: 'distinct',
      fields: ['dataSource.dimension', 'dataSource.dimensionY'],
      message: 'A matrix needs two different dimensions — rows and columns cannot be the same.',
    },
  ],
  consumes: [
    'dataSource.kind',
    'dataSource.dimension',
    'dataSource.dimensionY',
    'config.cellMetric',
    'dataSource.dateRange',
  ],
};

export const FUNNEL: PanelDef = {
  type: 'funnel',
  label: 'Funnel',
  description: 'Impressions → Clicks → Leads with drop-rates',
  defaultSize: { w: 4, h: 5 },
  kinds: ['perf'],
  fields: [F.dateRange(), F.compare(), F.kind(['perf'])],
  constraints: [],
  consumes: ['dataSource.kind', 'dataSource.dateRange'],
};

export const PACING: PanelDef = {
  type: 'pacing',
  label: 'Pacing',
  description: 'Budget + leads vs plan',
  defaultSize: { w: 12, h: 4 },
  kinds: ['pacing'],
  fields: [F.kind(['pacing'])],
  constraints: [],
  consumes: ['dataSource.kind'],
};

const hasTopojson = { field: 'config.topojsonUrl', isSet: true } as const;

export const MAP: PanelDef = {
  type: 'map',
  label: 'Map / postcode',
  description: 'Leads by postcode region',
  defaultSize: { w: 6, h: 5 },
  kinds: ['leads'],
  fields: [
    F.dateRange(),
    F.kind(['leads']),
    {
      path: 'config.topojsonUrl',
      label: 'TopoJSON URL',
      kind: 'text',
    },
    {
      path: 'config.geometryKey',
      label: 'Geometry key',
      kind: 'text',
      visibleWhen: hasTopojson,
    },
    {
      path: 'config.regionKeyField',
      label: 'Region key field',
      kind: 'text',
      visibleWhen: hasTopojson,
    },
    {
      path: 'config.keyPrefix',
      label: 'Key prefix',
      kind: 'number',
      min: 1,
      max: 10,
      visibleWhen: hasTopojson,
    },
    {
      path: 'config.centerLng',
      label: 'Map centre longitude',
      kind: 'number',
      visibleWhen: hasTopojson,
    },
    {
      path: 'config.centerLat',
      label: 'Map centre latitude',
      kind: 'number',
      visibleWhen: hasTopojson,
    },
    {
      path: 'config.scale',
      label: 'Scale',
      kind: 'number',
      visibleWhen: hasTopojson,
    },
    {
      path: 'config.colorScheme',
      label: 'Colour scheme',
      kind: 'colorScheme',
    },
  ],
  constraints: [],
  consumes: [
    'dataSource.kind',
    'config.topojsonUrl',
    'config.centerLng',
    'config.centerLat',
    'dataSource.dateRange',
  ],
};

export const TEXT: PanelDef = {
  type: 'text',
  label: 'Text',
  description: 'Static markdown',
  defaultSize: { w: 6, h: 2 },
  kinds: ['static'],
  fields: [
    {
      path: 'config.markdown',
      label: 'Markdown',
      kind: 'markdown',
      // Not '' — the validator treats an empty string as unset, so an empty
      // default would make every newly-added text panel born invalid. This is
      // the placeholder the old hand-written builder used.
      default: 'Click to edit text…',
      required: true,
    },
  ],
  constraints: [],
  consumes: ['config.markdown'],
};

export const FORMULA: PanelDef = {
  type: 'formula',
  label: 'Calculated metric',
  description: 'Formula: e.g. {Cost} / {Leads}',
  defaultSize: { w: 3, h: 2 },
  kinds: ['perf'],
  fields: [
    F.dateRange(),
    F.kind(['perf']),
    {
      path: 'config.formula',
      label: 'Formula',
      kind: 'formula',
      // A required field MUST declare a default, or a freshly-added panel is
      // born invalid and cannot be published. This is cost-per-lead, the same
      // expression the old hand-written builder seeded.
      default: '{Cost} / {Leads}',
      required: true,
    },
    {
      path: 'config.format',
      label: 'Format',
      kind: 'select',
      // Matches the default formula, which yields a currency.
      default: '$',
      required: true,
      options: {
        kind: 'static',
        options: FORMATS.map((f) => ({ value: f.key, label: f.label })),
      },
    },
    F.threshold(),
  ],
  constraints: [],
  consumes: ['dataSource.kind', 'config.formula', 'config.format', 'dataSource.dateRange'],
};
